import BigDataChart from "@/components/ui/chart/BigDataChart";
import ChartLoadingWrapper from "@/components/ui/chart/ChartLoadingWrapper";
import DonutChart from "@/components/ui/chart/DonutChart";
import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import LineChart from "@/components/ui/chart/LineChart";
import VerticalBarChart from "@/components/ui/chart/VerticalBarChart";
import RetryErrorState from "@/components/ui/RetryErrorState";
import { MESSAGE } from "@/constants/message";
import useDatasetWidgetSummary from "@/hooks/dataset-widget/useDatasetWidgetSummary";
import { WidgetConfig, WidgetType } from "@/types/widget";

import WidgetConfigurationPopover from "./widgetConfiguration/WidgetConfigurationPopover";

interface DatasetWidgetCardProps {
  widget: WidgetConfig;
  onOpenWidgetConfig: (id: string) => void;
  onRemoveWidget: (id: string) => void;
  /** Defaults to widget.label when omitted. */
  name?: string;
  /** When provided the card fetches via the analysis-scoped API instead of the regular dataset API. */
  analysisId?: string;
  /** When true, disables the Remove action while a remove API call is in flight. */
  isRemovePending?: boolean;
}

export default function DatasetWidgetCard({
  widget,
  onOpenWidgetConfig,
  onRemoveWidget,
  name,
  analysisId,
  isRemovePending = false
}: Readonly<DatasetWidgetCardProps>) {
  const {
    rawData,
    bigNumberData,
    groupedData,
    invalidWidget,
    analysisError,
    refetchAnalysisWidget,
    isChartLoading,
    isStructureChange,
    lineSeries
  } = useDatasetWidgetSummary(widget, { analysisId });

  const hasGroupedData = groupedData
    ? groupedData.rows.length > 0
    : rawData.length > 0;

  const isinValidWidgetData = invalidWidget || isStructureChange;

  return (
    <div className="border border-gainsboro rounded-lg p-3">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-txtHeading">
            {name ?? widget.label}
          </span>
          <div className="flex flex-row items-center">
            <WidgetConfigurationPopover
              onOpenWidgetConfig={() => onOpenWidgetConfig(widget.widget_id)}
              onRemoveWidget={() => onRemoveWidget(widget.widget_id)}
              isRemovePending={isRemovePending}
            />
          </div>
        </div>
        {isinValidWidgetData && (
          <div className="flex flex-col justify-center gap-2 items-center h-[400px] px-4">
            <p className="text-base text-txtHeading font-semibold text-center">
              {MESSAGE.WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_TITLE}
            </p>
            <p className="text-sm text-txtBody text-center">
              {invalidWidget
                ? MESSAGE.WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_DESCRIPTION
                : MESSAGE.WIDGET_CARD_STRUCTURE_CHANGE_DESCRIPTION}
            </p>
          </div>
        )}
        {analysisError && analysisId && !isinValidWidgetData && (
          <RetryErrorState onRetry={refetchAnalysisWidget} />
        )}

        {!isinValidWidgetData && !analysisError && (
          <ChartLoadingWrapper
            isLoading={isChartLoading}
            chartType={widget.chart_type}
          >
            {((widget.chart_type !== WidgetType.BIG_NUMBER &&
              !hasGroupedData) ||
              (widget.chart_type === WidgetType.BIG_NUMBER &&
                !bigNumberData)) && (
              <div className="flex-center h-[400px] px-4">
                <p className="text-base text-txtHeading font-semibold text-center">
                  {MESSAGE.WIDGET_CARD_NO_DATA_AVAILABLE}
                </p>
              </div>
            )}
            {widget.chart_type === WidgetType.BIG_NUMBER && bigNumberData && (
              <BigDataChart
                value={bigNumberData.value}
                type={bigNumberData.type}
                measure={bigNumberData.measure}
              />
            )}
            {widget.chart_type === WidgetType.BAR_H &&
              (groupedData
                ? !!groupedData.rows.length && (
                    <HorizontalBarChart
                      data={groupedData.rows}
                      categoryKey={groupedData.nameKey}
                      series={groupedData.series}
                      height={450}
                    />
                  )
                : !!rawData.length && (
                    <HorizontalBarChart
                      data={rawData.map((d) => ({
                        label: d.label,
                        value: Number(d.value)
                      }))}
                      categoryKey="label"
                      series={lineSeries}
                      aggregation={widget.aggregation}
                      height={450}
                    />
                  ))}
            {widget.chart_type === WidgetType.DONUT && !!rawData.length && (
              <DonutChart
                data={rawData.map((item) => ({
                  name: item.label,
                  value: Number(item.value)
                }))}
                height={450}
              />
            )}
            {widget.chart_type === WidgetType.BAR_V &&
              (groupedData
                ? !!groupedData.rows.length && (
                    <VerticalBarChart
                      data={groupedData.rows}
                      categoryKey={groupedData.nameKey}
                      series={groupedData.series}
                      isResponsive={false}
                      height={450}
                    />
                  )
                : !!rawData.length && (
                    <VerticalBarChart
                      data={rawData.map((d) => ({
                        label: d.label,
                        value: Number(d.value)
                      }))}
                      categoryKey="label"
                      series={lineSeries}
                      aggregation={widget.aggregation}
                      height={450}
                    />
                  ))}
            {widget.chart_type === WidgetType.LINE &&
              (groupedData
                ? !!groupedData.rows.length && (
                    <LineChart
                      data={groupedData.rows}
                      categoryKey={groupedData.nameKey}
                      series={groupedData.series}
                      height={450}
                    />
                  )
                : !!rawData.length && (
                    <LineChart
                      data={rawData.map((d) => ({
                        label: d.label,
                        value: Number(d.value)
                      }))}
                      categoryKey="label"
                      series={lineSeries}
                      aggregation={widget.aggregation}
                      height={450}
                    />
                  ))}
          </ChartLoadingWrapper>
        )}
      </div>
    </div>
  );
}
